import { Injectable } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
  IStockImage,
  IStockImageCropSize,
  IStockImageProvider,
  IStockImageSearchResult,
  StockImageAspectRatio,
  StockImageProviderName,
} from '../../../interfaces/stock-image.interface';
import { STOCK_IMAGE_STRINGS } from '../../../config/stock-image.constant';

interface IPexelsPhotoSource {
  original: string;
  large2x: string;
  large: string;
  medium: string;
  small: string;
  tiny: string;
}

interface IPexelsPhoto {
  id: number;
  width: number;
  height: number;
  url: string;
  photographer: string;
  photographer_url: string;
  alt: string;
  src: IPexelsPhotoSource;
}

interface IPexelsSearchResponse {
  photos: IPexelsPhoto[];
  total_results: number;
  page: number;
  per_page: number;
  next_page?: string;
}

@Injectable({ providedIn: 'root' })
export class PexelsStockImageProvider implements IStockImageProvider {
  readonly name: StockImageProviderName = 'pexels';
  readonly displayName = 'Pexels';
  readonly requiresAttribution = true;
  readonly attributionUrl = 'https://www.pexels.com';

  private readonly searchEndpoint = 'https://api.pexels.com/v1/search';
  private readonly apiKey = environment.stockImage?.pexelsApiKey ?? '';

  isConfigured(): boolean {
    return !!this.apiKey;
  }

  search(
    query: string,
    page: number,
    perPage: number,
    aspectRatio: StockImageAspectRatio = 'original',
  ): Observable<IStockImageSearchResult> {
    if (!this.isConfigured()) {
      return throwError(() => new Error(STOCK_IMAGE_STRINGS.NOT_CONFIGURED));
    }

    const url = `${this.searchEndpoint}?query=${encodeURIComponent(
      query,
    )}&page=${page}&per_page=${perPage}${this.orientationParamFor(aspectRatio)}`;

    // Deliberately native fetch rather than HttpClient: AuthInterceptor sets an
    // Authorization header on every HttpClient request, which would both
    // overwrite the Pexels API key and leak the user's Auth0 token to Pexels.
    return from(
      fetch(url, {
        method: 'GET',
        headers: { Authorization: this.apiKey },
      }),
    ).pipe(
      switchMap((response: Response) => {
        if (!response.ok) {
          return throwError(() => new Error(this.errorFor(response.status)));
        }
        return from(response.json() as Promise<IPexelsSearchResponse>);
      }),
      map((response: IPexelsSearchResponse) =>
        this.toSearchResult(response, page, perPage),
      ),
    );
  }

  /**
   * Narrows the search to landscape photos when that is the shape we need.
   * Cover asks for it too: a 3:1 banner cut out of a portrait photo keeps only
   * a thin band of it, so the pool is limited to photos that are already wide.
   *
   * Deliberately not done for square: Pexels only counts an exactly 1:1 photo
   * as square, and that pool is tiny next to the unfiltered one - "volunteers"
   * returns 20 results against 5,511, less than a single page. Square is
   * handled by cropping instead, which keeps the whole library searchable.
   */
  private orientationParamFor(aspectRatio: StockImageAspectRatio): string {
    return aspectRatio === 'landscape' || aspectRatio === 'cover'
      ? '&orientation=landscape'
      : '';
  }

  /**
   * Pexels serves its photos through an image CDN that crops on the fly, so a
   * photo can be delivered at the exact ratio the target box needs rather than
   * being cropped by the browser or letterboxed.
   *
   * No `crop=` mode is passed on purpose. Pexels does not honour the smart crop
   * modes - `crop=faces` behaves like an entropy crop and happily cuts a
   * subject's head off on a portrait photo, where the default centre crop keeps
   * it.
   */
  cropUrl(image: IStockImage, size: IStockImageCropSize): string {
    // Every Pexels `src` is the same file with different query params, so
    // dropping the query gives back the untouched original to crop from.
    const [baseUrl] = image.downloadUrl.split('?');
    return `${baseUrl}?auto=compress&cs=tinysrgb&fit=crop&w=${size.width}&h=${size.height}`;
  }

  private errorFor(status: number): string {
    if (status === 401 || status === 403) {
      return STOCK_IMAGE_STRINGS.INVALID_KEY;
    }
    if (status === 429) {
      return STOCK_IMAGE_STRINGS.RATE_LIMITED;
    }
    return STOCK_IMAGE_STRINGS.SEARCH_FAILED;
  }

  private toSearchResult(
    response: IPexelsSearchResponse,
    page: number,
    perPage: number,
  ): IStockImageSearchResult {
    const images = (response.photos ?? []).map((photo) => this.toImage(photo));
    return {
      images,
      page: response.page ?? page,
      perPage: response.per_page ?? perPage,
      totalResults: response.total_results ?? images.length,
      hasMore: !!response.next_page,
    };
  }

  private toImage(photo: IPexelsPhoto): IStockImage {
    return {
      id: `${photo.id}`,
      providerName: this.name,
      thumbnailUrl: photo.src?.medium ?? photo.src?.small ?? photo.src?.tiny,
      previewUrl: photo.src?.large ?? photo.src?.medium,
      // large2x rather than original: originals run to several MB each and the
      // uploader enforces a 25MB total budget across the whole gallery.
      downloadUrl: photo.src?.large2x ?? photo.src?.large ?? photo.src?.original,
      width: photo.width,
      height: photo.height,
      description: photo.alt || '',
      photographerName: photo.photographer || '',
      photographerUrl: photo.photographer_url || '',
      providerUrl: photo.url || '',
    };
  }
}
